<template>
  <wizard-form v-on="$listeners" v-bind="$attrs">
    <template #content>
      <v-row>
        <v-col cols="12" class="mb-5">
          <h1 class="primary--text font-weight-medium">
            {{ operation == 'W' ? $t('transfer_request_withdrawal') : $t('transfer_request_deposit') }}
          </h1>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_available_funds')"
            rules="required"
            v-slot="{ errors }"
          >
            <numeric-input
              :label="$t('transfer_available_funds')"
              dense
              autocomplete="nope"
              :value="availableFunds.cashAvailable"
              :error-messages="errors[0]"
              hide-details="auto"
              readonly
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_available_funds_with_financing')"
            rules="required"
            v-slot="{ errors }"
          >
            <numeric-input
              :label="$t('transfer_available_funds_with_financing')"
              dense
              autocomplete="nope"
              :value="availableFunds.cashAndMargin"
              :error-messages="errors[0]"
              hide-details="auto"
              readonly
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_request_currency')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              v-model="clientRequest.currency"
              :items="currencies"
              :label="`${$t('transfer_request_currency')} *`"
              :error-messages="errors[0]"
              hide-details="auto"
              dense
              return-object
              item-text="currencyName"
            >
            </v-select>
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_request_amount')"
            :rules="{
              required: true,
              min_value: 0.01
            }"
            v-slot="{ errors }"
          >
            <numeric-input
              :label="`${$t('transfer_request_amount')} *`"
              dense
              autocomplete="nope"
              v-model="clientRequest.amount"
              :error-messages="errors[0]"
              hide-details="auto"
              maxlength="50"
            />
          </ValidationProvider>
        </v-col>
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_request_start_date')"
            rules="required"
            v-slot="{ errors }"
          >
            <date-picker-input
              :label="`${$t('transfer_request_start_date')} *`"
              v-model="clientRequest.schedule.start"
              dense
              :min="minDate"
              :allowedDates="allowedDates"
              :error-messages="errors[0]"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row
        v-if="
          clientRequest.currency &&
            clientRequest.currency.currencyCode !== 'USD'
        "
      >
        <v-col cols="12">
          <h3>{{ $t('transfer_request_base_currency') }}</h3>
          <ValidationProvider
            :name="$t('transfer_request_usd_funds')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-radio-group
              v-model="clientRequest.dollarFunds"
              :error-messages="errors[0]"
            >
              <v-radio
                :label="$t('transfer_request_usd_funds')"
                :value="true"
              ></v-radio>
              <v-radio
                :label="$t('transfer_request_currency_funds')"
                :value="false"
              ></v-radio>
            </v-radio-group>
          </ValidationProvider>
        </v-col>
      </v-row>
      <v-row v-if="!clientRequest.permanentInstruction.fromIndex">
        <v-col cols="12" md="6">
          <ValidationProvider
            :name="$t('transfer_request_instruction')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              v-model="clientRequest.instructionType"
              :items="options"
              :label="`${$t('transfer_request_instruction')} *`"
              :error-messages="errors[0]"
              @input="handleInstructionType"
              dense
            >
              <template #item="{ item }">
                <span>{{ $t(item) }}</span>
              </template>

              <template #selection="{ item }">
                <span>{{ $t(item) }}</span>
              </template>
            </v-select>
          </ValidationProvider>
        </v-col>
      </v-row>
    </template>
  </wizard-form>
</template>
<script>
import WizardForm from '@/components/commons/WizardForm'
import DatePickerInput from '@/components/commons/DatePickerInput'
import NumericInput from '@/components/commons/NumericInput'
import { mapState } from 'vuex'
import { hasPermission } from '@/utils'

const CREATE_INSTRUCTION = 'transfer_request_new_instruction'
const GET_INSTRUCTIONS = 'transfer_request_existing_instruction'

export default {
  components: {
    WizardForm,
    DatePickerInput,
    NumericInput
  },
  props: {
    clientRequest: {
      type: Object,
      require: true
    },
    availableFunds: {
      type: Object,
      require: true
    },
    currencies: {
      type: Array,
      require: true
    },
    operation: {
      type: String,
      require: true
    },
    instructions: {
      type: Array,
      require: true
    },
    executive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      stepTwo: {
        account_type: 'Cuentas de Bancos en Panamá'
      },
      banks: ['Banco Mercantil', 'Banesco']
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('account', ['account', 'accountData']),
    isExecutive() {
      return (
        this.user?.authorityLevelsCode === 1 ||
        this.user?.authorityLevelsCode === 2 ||
        this.user?.authorityLevelsCode === 4
      )
    },
    isAccountCode() {
      return (
        this.user?.avobjects?.findIndex(
          item =>
            item.code === this.account.code && item.typeCode === 'accountCode'
        ) > -1
      )
    },
    canCreate() {
      return this.hasPermission('create-third-party-permanent-instructions') ||
        this.hasPermission('create-permanent-instructions')
    },
    canGetInstructions() {
      return this.hasPermission('view-permanent-instructions')
    },
    options() {
      const elements = []
      if ((this.isAccountCode && this.canCreate) || this.isExecutive) {
        elements.push(CREATE_INSTRUCTION)
      }

      if (
        (this.canGetInstructions || this.isExecutive) &&
        this.instructions.length
      ) {
        elements.push(GET_INSTRUCTIONS)
      }
      return elements
    },
    minDate() {
      return this.$date().format('YYYY-MM-DD')
    },
    allowedDates() {
      return val => this.getDay(val) !== 5 && this.getDay(val) !== 6
    }
  },
  methods: {
    getDay(date) {
      return new Date(date).getDay()
    },
    handleInstructionType() {
      this.clientRequest.permanentInstruction = {}
    },
    hasPermission(permission) {
      return hasPermission(this.user, permission)
    }
  }
}
</script>
<style scoped>
.main-container {
  line-height: normal;
}

hr {
  margin: 0;
  padding: 0;
}

hr.rounded {
  border-top: 2px solid;
  width: 400px;
}
</style>
